import { useState } from "react";
import { Search, User, X } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export function EditFollisPanel() {
  // Specific-user editing state
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState(null);
  const [userAmount, setUserAmount] = useState("");
  const [userBusy, setUserBusy] = useState(false);

  const search = async (val) => {
    setQ(val); setPicked(null);
    if (val.trim().length < 2) { setResults(null); return; }
    try {
      const r = await api.get(`/admin/managers?q=${encodeURIComponent(val.trim())}&limit=8`);
      setResults(r.data.managers || []);
    } catch { setResults([]); }
  };

  const setUserFollis = async () => {
    const amt = parseInt(userAmount, 10);
    if (!picked) return toast.error("Select a player first");
    if (isNaN(amt) || amt < 0) return toast.error("Enter a valid Follis amount (0 or greater)");
    setUserBusy(true);
    try {
      const r = await api.post("/admin/set-follis-user", { user_id: picked.user_id, amount: amt });
      toast.success(
        `Set ${r.data.name}'s Follis to ${amt.toLocaleString()} 🪙`,
        { icon: <GoldCoin size={18} className="text-amber-600" /> }
      );
      setUserAmount(""); setPicked(null); setQ(""); setResults(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setUserBusy(false); }
  };

  return (
    <div data-testid="edit-follis-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <GoldCoin size={16} className="text-amber-600" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Edit Player Follis</h3>
      </div>
      
      <div className="flex flex-wrap items-start gap-3">
        <div className="relative flex-1 min-w-[220px]">
          {picked ? (
            <div data-testid="edit-follis-user-picked" className="flex items-center justify-between gap-2 bg-zinc-900 border border-amber-700/50 px-3 py-2 rounded-sm">
              <span className="text-sm text-amber-200 truncate">{picked.name} <span className="text-zinc-500">· {picked.email}</span></span>
              <button data-testid="edit-follis-user-clear" onClick={() => { setPicked(null); setQ(""); setResults(null); }} className="text-zinc-500 hover:text-amber-400">
                <X size={15} />
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-2.5 text-zinc-600" />
                <input
                  data-testid="edit-follis-search"
                  type="text"
                  value={q}
                  onChange={(e) => search(e.target.value)}
                  placeholder="Search by name or email"
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm focus:border-amber-600 outline-none rounded-sm"
                />
              </div>
              {results && results.length > 0 && (
                <div data-testid="edit-follis-results" className="absolute z-10 w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-sm max-h-64 overflow-y-auto shadow-xl">
                  {results.map((m) => (
                    <button
                      key={m.user_id}
                      data-testid={`edit-follis-result-${m.user_id}`}
                      onClick={() => { setPicked(m); setResults(null); }}
                      className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-800 border-b border-zinc-800 last:border-0"
                    >
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-zinc-500">{m.email}</div>
                    </button>
                  ))}
                </div>
              )}
              {results && results.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-sm text-zinc-500">
                  No players found
                </div>
              )}
            </>
          )}
        </div>
        <input
          data-testid="edit-follis-amount"
          type="number"
          min={0}
          value={userAmount}
          onChange={(e) => setUserAmount(e.target.value)}
          placeholder="Follis amount"
          disabled={!picked}
          className="w-32 bg-zinc-900 border border-zinc-800 px-3 py-2 text-amber-200 text-sm focus:border-amber-600 outline-none rounded-sm disabled:opacity-50"
        />
        <button
          data-testid="edit-follis-btn"
          onClick={setUserFollis}
          disabled={userBusy || !picked}
          className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50"
        >
          Set Follis
        </button>
      </div>
      <p className="text-xs text-zinc-500 mt-2">
        Sets the player&apos;s Follis to the exact amount entered (does not add/subtract, but replaces current value).
      </p>
    </div>
  );
}
